import {
  Badge,
  Button,
  Card,
  CardContent,
  DetailGrid,
  DetailItem,
  Loading,
} from '@startupquickstart/frontend';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import {
  CalendarClock,
  Clock,
  Mail,
  QrCode,
  Shield,
  ShieldOff,
  User,
} from 'lucide-react';
import { useParams } from 'react-router-dom';

import { getScanAccessState } from './guestAccessStatus';
import { useGuestAccessActions } from './useGuestAccessActions';
import { useHotelUserContext } from './useHotelUserContext';

function formatDate(dateString: string | null): string {
  if (!dateString) return 'N/A';
  return format(new Date(dateString), 'MMM d, yyyy');
}

function formatDateTime(dateString: string | null): string {
  if (!dateString) return 'N/A';
  return format(new Date(dateString), 'MMM d, yyyy h:mm a');
}

function AccessStateBadge({ state }: { state: ReturnType<typeof getScanAccessState> }) {
  if (state === 'revoked') {
    return (
      <Badge variant="destructive">
        <ShieldOff className="h-3 w-3 mr-1" />
        Revoked
      </Badge>
    );
  }
  if (state === 'active') {
    return (
      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
        <Shield className="h-3 w-3 mr-1" />
        Active
      </Badge>
    );
  }
  return (
    <Badge variant="secondary">
      <ShieldOff className="h-3 w-3 mr-1" />
      Expired
    </Badge>
  );
}

export function HotelUserOverviewTab() {
  const { hotelId, userId } = useParams<{ hotelId: string; userId: string }>();
  const { userData, isLoading } = useHotelUserContext();
  const access = useGuestAccessActions({ hotelId, userId });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loading />
      </div>
    );
  }

  if (!userData) return null;

  const mostRecentScan = userData.scans[0] ?? null;
  const accessState = mostRecentScan ? getScanAccessState(mostRecentScan) : null;
  const totalScans = userData.scans.length;

  return (
    <div className="space-y-6">
      {/* Guest info (2/3) beside the most-recent-scan access card (1/3). */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 items-stretch">
        {/* Combined informational card: guest contact details + current access */}
        <Card
          className={cn(
            'rounded-xl border bg-card shadow-sm py-4 gap-0 flex flex-col',
            mostRecentScan ? 'lg:col-span-2' : 'lg:col-span-3',
          )}
        >
          <CardContent className="space-y-4">
            <div>
              <h3 className="text-lg font-medium tracking-tight text-zinc-900">Guest Information</h3>
              <p className="text-sm text-muted-foreground">Contact details and current access</p>
            </div>
            <DetailGrid className="grid-cols-2 lg:grid-cols-3">
              <DetailItem label="Name" icon={<User className="h-4 w-4" />}>
                {userData.user.firstName || userData.user.lastName
                  ? `${userData.user.firstName || ''} ${userData.user.lastName || ''}`.trim()
                  : 'Not provided'}
              </DetailItem>
              <DetailItem label="Email" icon={<Mail className="h-4 w-4" />}>
                {userData.user.email}
              </DetailItem>
              <DetailItem
                label="Status"
                icon={
                  accessState === 'active' ? (
                    <Shield className="h-4 w-4" />
                  ) : (
                    <ShieldOff className="h-4 w-4" />
                  )
                }
              >
                {accessState ? (
                  <AccessStateBadge state={accessState} />
                ) : (
                  <Badge variant="secondary">No access</Badge>
                )}
              </DetailItem>
              <DetailItem label="Access Gained" icon={<Clock className="h-4 w-4" />}>
                {mostRecentScan ? formatDateTime(mostRecentScan.scannedAt) : 'N/A'}
              </DetailItem>
              <DetailItem label="Expires" icon={<CalendarClock className="h-4 w-4" />}>
                {mostRecentScan ? formatDate(mostRecentScan.expiresAt) : 'N/A'}
              </DetailItem>
              <DetailItem label="Total Scans" icon={<QrCode className="h-4 w-4" />}>
                {totalScans}
              </DetailItem>
            </DetailGrid>
          </CardContent>
        </Card>

        {/* Most Recent Scan — access management hub (1/3, stacked for narrow column) */}
        {mostRecentScan && accessState && (
          <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0 flex flex-col">
            <CardContent className="flex flex-1 flex-col p-0">
              <div className="border-b px-6 py-4">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-medium tracking-tight text-zinc-900">
                    Most Recent Scan
                  </h3>
                  <AccessStateBadge state={accessState} />
                </div>
                <p className="text-sm text-muted-foreground">
                  Manage this guest&apos;s access to the hotel.
                </p>
              </div>
              <div className="flex flex-1 flex-col gap-4 px-6 py-4">
                <DetailGrid className="grid-cols-1 gap-3">
                  <DetailItem label="Scanned At" icon={<Clock className="h-4 w-4" />}>
                    {formatDateTime(mostRecentScan.scannedAt)}
                  </DetailItem>
                  <DetailItem label="Access Code" icon={<QrCode className="h-4 w-4" />}>
                    {mostRecentScan.accessCode?.name || 'N/A'}
                  </DetailItem>
                  <DetailItem label="Expires" icon={<CalendarClock className="h-4 w-4" />}>
                    {formatDate(mostRecentScan.expiresAt)}
                  </DetailItem>
                </DetailGrid>
                <div className="mt-auto flex flex-col gap-2 pt-2">
                  <Button
                    variant="default"
                    className="w-full"
                    icon={CalendarClock}
                    onClick={() => access.openAdjust(mostRecentScan)}
                  >
                    Adjust expiration
                  </Button>
                  {accessState === 'active' && (
                    <Button
                      variant="outline"
                      className="w-full text-destructive hover:text-destructive"
                      icon={ShieldOff}
                      onClick={() => access.openRevoke(mostRecentScan)}
                    >
                      Revoke access
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {access.dialogs}
    </div>
  );
}
